import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";

// Backend-controlled per-page SEO: title, meta, OG, canonical, robots, JSON-LD.
export default function SEOHead() {
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    const path = location.pathname || "/";

    const setMeta = (attr, key, content) => {
      if (content == null) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const setLink = (rel, href) => {
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    (async () => {
      try {
        const { data } = await api.get(`/seo/resolve`, { params: { path } });
        if (cancelled) return;
        document.title = data.title;
        setMeta("name", "description", data.description);
        setMeta("name", "robots", data.noindex ? "noindex,follow" : "index,follow");
        setMeta("property", "og:site_name", "StrongKind Circle");
        setMeta("property", "og:title", data.title);
        setMeta("property", "og:description", data.description);
        setMeta("property", "og:type", data.type || "website");
        setMeta("property", "og:url", data.canonical);
        setMeta("property", "og:image", data.og_image || `${window.location.origin}/og-image.jpg`);
        setMeta("name", "twitter:card", "summary_large_image");
        setMeta("name", "twitter:title", data.title);
        setMeta("name", "twitter:description", data.description);
        setMeta("name", "twitter:image", data.og_image || `${window.location.origin}/og-image.jpg`);
        if (data.canonical) setLink("canonical", data.canonical);

        const prev = document.getElementById("ld-json");
        if (prev) prev.remove();
        if (data.json_ld) {
          const s = document.createElement("script");
          s.type = "application/ld+json";
          s.id = "ld-json";
          s.text = JSON.stringify(data.json_ld);
          document.head.appendChild(s);
        }

        const prevBc = document.getElementById("ld-breadcrumb");
        if (prevBc) prevBc.remove();
        if (data.breadcrumb) {
          const b = document.createElement("script");
          b.type = "application/ld+json";
          b.id = "ld-breadcrumb";
          b.text = JSON.stringify(data.breadcrumb);
          document.head.appendChild(b);
        }

        const prevFaq = document.getElementById("ld-faq");
        if (prevFaq) prevFaq.remove();
        if (data.faqpage) {
          const fq = document.createElement("script");
          fq.type = "application/ld+json";
          fq.id = "ld-faq";
          fq.text = JSON.stringify(data.faqpage);
          document.head.appendChild(fq);
        }
      } catch {
        /* non-fatal */
      }
    })();

    return () => { cancelled = true; };
  }, [location.pathname]);

  return null;
}
